import { useCallback, useEffect, useRef, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '@/lib/firebase';

// Phone sign-in without Google's reCAPTCHA. The API texts the code (Twilio
// Verify) and checks it, then hands back a Firebase custom token for the
// number's Firebase user; the browser signs in with that and carries on
// through signInWeb as before. See proxi-api services/auth/phoneVerification.

export type PhoneAuthStep = 'enter-phone' | 'enter-code' | 'success';

export interface PhoneAuthState {
  step: PhoneAuthStep;
  phoneE164: string | null;
  isSending: boolean;
  isVerifying: boolean;
  error: string | null;
  resendCooldown: number;
}

export interface PhoneAuthHandlers {
  sendCode: (phoneE164: string) => Promise<void>;
  verifyCode: (code: string) => Promise<string | null>;
  resendCode: () => Promise<void>;
  reset: () => void;
  clearError: () => void;
}

const RESEND_COOLDOWN_SECONDS = 30;

export const SEND_PHONE_CODE = gql`
  mutation SendPhoneCode($phone: String!) {
    sendPhoneCode(phone: $phone) {
      code
      success
      error
      retryAfterSeconds
    }
  }
`;

export const CHECK_PHONE_CODE = gql`
  mutation CheckPhoneCode($phone: String!, $code: String!) {
    checkPhoneCode(phone: $phone, code: $code) {
      code
      success
      error
      customToken
      phone
    }
  }
`;

interface ApiOutcome {
  success?: boolean | null;
  error?: string | null;
  customToken?: string | null;
}

export function usePhoneAuth(): { state: PhoneAuthState; handlers: PhoneAuthHandlers } {
  const [send] = useMutation(SEND_PHONE_CODE);
  const [check] = useMutation(CHECK_PHONE_CODE);
  // One send at a time: a double tap must not text two codes.
  const sendingRef = useRef(false);

  const [step, setStep] = useState<PhoneAuthStep>('enter-phone');
  const [phoneE164, setPhoneE164] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = window.setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => window.clearTimeout(id);
  }, [resendCooldown]);

  const sendCode = useCallback(
    async (e164: string) => {
      if (sendingRef.current) return;
      sendingRef.current = true;
      setError(null);
      setIsSending(true);
      try {
        const res = await send({ variables: { phone: e164 } });
        const outcome: ApiOutcome | undefined = res.data?.sendPhoneCode;
        if (!outcome?.success) {
          setError(outcome?.error || 'The code could not be sent. Please try again.');
          return;
        }
        setPhoneE164(e164);
        setStep('enter-code');
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      } catch (err) {
        console.error('Phone auth: sending the code failed', err);
        setError('The code could not be sent. Please try again.');
      } finally {
        sendingRef.current = false;
        setIsSending(false);
      }
    },
    [send],
  );

  const resendCode = useCallback(async () => {
    if (!phoneE164 || resendCooldown > 0) return;
    await sendCode(phoneE164);
  }, [phoneE164, resendCooldown, sendCode]);

  const verifyCode = useCallback(
    async (code: string): Promise<string | null> => {
      setError(null);
      if (!phoneE164) {
        setError('Please request a code first.');
        return null;
      }
      setIsVerifying(true);
      try {
        const res = await check({ variables: { phone: phoneE164, code } });
        const outcome: ApiOutcome | undefined = res.data?.checkPhoneCode;
        if (!outcome?.success || !outcome.customToken) {
          setError(outcome?.error || 'The code could not be checked. Please try again.');
          return null;
        }
        const credential = await signInWithCustomToken(auth, outcome.customToken);
        const token = await credential.user.getIdToken(true);
        setStep('success');
        return token;
      } catch (err) {
        console.error('Phone auth: checking the code failed', err);
        setError('The code could not be checked. Please try again.');
        return null;
      } finally {
        setIsVerifying(false);
      }
    },
    [check, phoneE164],
  );

  const reset = useCallback(() => {
    setStep('enter-phone');
    setPhoneE164(null);
    setError(null);
    setResendCooldown(0);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    state: { step, phoneE164, isSending, isVerifying, error, resendCooldown },
    handlers: { sendCode, verifyCode, resendCode, reset, clearError },
  };
}
